import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Icon } from "../../../components/Icon/Icon";
import { AudioPlayerProps } from "./AudioPlayer.types";
import {
  AudioUploadBar,
  AudioUploadRow,
  ProgressDot,
  WaveformWrap,
} from "./AudioPlayer.styles";
import { FlexRow } from "../../../components/Layout/FlexRow/FlexRow";
import { colorPalette } from "../../../theme/colors";
import { FlexColumn } from "../../../components/Layout/FlexColumn/FlexColumn";
import Box from "../../../primitives/Box/Box";
import { Body5RegularTypography } from "../../../primitives/Typography/typographyVariants";
import {
  barsFromAudioBuffer,
  clamp01,
  DEFAULT_BAR_COUNT,
  formatTime,
  normalizeBars,
  PLAY_EVENT,
} from "../../../utils/constants";

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src,
  bars,
  barCount = DEFAULT_BAR_COUNT,
  showTime = true,
  timeMode = "auto",
  disabled = false,
  groupId = "default",
  playerId = src,
  onPlay,
  onPause,
  onEnded,
  dataCy = "audio-player",
  onTimeChange,
  loading = false,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [currentTimeSec, setCurrentTimeSec] = useState(0);
  const [durationSec, setDurationSec] = useState(0);

  const [computedBars, setComputedBars] = useState<number[] | null>(null);

  const [isScrubbing, setIsScrubbing] = useState(false);
  const [isLoadingWaveform, setIsLoadingWaveform] = useState(false);

  const BAR_PX = 2;
  const GAP_PX = 2;
  const MIN_BARS = 8;

  const [autoBarCount, setAutoBarCount] = useState<number | null>(null);

  const effectiveBarCount = useMemo(() => {
    return Math.max(MIN_BARS, autoBarCount ?? barCount);
  }, [autoBarCount, barCount]);

  const cleanupObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const effectiveBars = useMemo(() => {
    const provided = bars?.length ? normalizeBars(bars) : null;
    const generated = computedBars?.length ? computedBars : null;

    // Placeholder must be SAME length as effectiveBarCount to avoid layout shift
    const count = Math.max(8, effectiveBarCount);
    const placeholder = new Array(count)
      .fill(0)
      .map((_, i) => 0.25 + 0.25 * Math.abs(Math.sin(i * 0.55)));

    return provided ?? generated ?? placeholder;
  }, [bars, computedBars, effectiveBarCount]);

  const progress01 = useMemo(() => {
    if (!durationSec) return 0;
    return clamp01(currentTimeSec / durationSec);
  }, [currentTimeSec, durationSec]);

  const activeBarIndex = useMemo(() => {
    const len = effectiveBars.length;
    if (len === 0) return -1;

    // treat each bar as "played" once the dot crosses its center
    const idx = Math.floor(progress01 * len - 0.5);
    return Math.max(-1, Math.min(len - 1, idx));
  }, [progress01, effectiveBars.length]);

  const displayTime = useMemo(() => {
    if (!showTime || timeMode === "none") return "";

    const remaining = Math.max(0, (durationSec || 0) - (currentTimeSec || 0));

    if (timeMode === "duration") return formatTime(durationSec);
    if (timeMode === "current") return formatTime(remaining);

    // auto -> show remaining in both states
    return formatTime(remaining);
  }, [showTime, timeMode, currentTimeSec, durationSec]);

  const broadcastPlay = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent(PLAY_EVENT, { detail: { groupId, playerId } })
    );
  }, [groupId, playerId]);

  const togglePlayPause = useCallback(() => {
    if (loading || !src) return;
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      // iPhone: call play() FIRST (must be synchronous in gesture handler)
      const playPromise = audio.play();

      // Then update state
      setHasInteracted(true);
      broadcastPlay();

      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => onPlay?.())
          .catch((err) => {
            console.error("Error playing audio", err);
          });
      } else {
        onPlay?.();
      }
    } else {
      audio.pause();
      onPause?.();
    }
  }, [broadcastPlay, onPlay, onPause]);

  const seekToClientX = useCallback(
    (clientX: number) => {
      const audio = audioRef.current;
      const el = waveformRef.current;
      if (!audio || !el || !durationSec) return;

      const rect = el.getBoundingClientRect();
      const pct = clamp01((clientX - rect.left) / rect.width);
      const nextTime = pct * durationSec;

      audio.currentTime = nextTime;
      setCurrentTimeSec(nextTime);
    },
    [durationSec]
  );

  const onPointerDownWaveform = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      setHasInteracted(true);
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      setIsScrubbing(true);
      seekToClientX(e.clientX);
    },
    [disabled, seekToClientX]
  );

  const onPointerMoveWaveform = useCallback(
    (e: React.PointerEvent) => {
      if (!isScrubbing) return;
      seekToClientX(e.clientX);
    },
    [isScrubbing, seekToClientX]
  );

  const onPointerUpWaveform = useCallback(() => {
    setIsScrubbing(false);
  }, []);

  const showDot = hasInteracted;

  useEffect(() => {
    const el = waveformRef.current;
    if (!el) return;
    if (typeof ResizeObserver === "undefined") return;

    const compute = (width: number) => {
      // count*BAR_PX + (count-1)*GAP_PX <= width  =>  count <= (width+GAP_PX)/(BAR_PX+GAP_PX)
      const next = Math.max(
        MIN_BARS,
        Math.floor((width + GAP_PX) / (BAR_PX + GAP_PX))
      );
      setAutoBarCount((prev) => (prev === next ? prev : next));
    };

    compute(el.getBoundingClientRect().width);

    const ro = new ResizeObserver((entries) => {
      const w =
        entries[0]?.contentRect?.width ?? el.getBoundingClientRect().width;
      compute(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Pause when another player in same group starts playing
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ groupId: string; playerId: string }>;
      if (!ev.detail) return;
      if (ev.detail.groupId !== groupId) return;
      if (ev.detail.playerId === playerId) return;

      const audio = audioRef.current;
      if (audio && !audio.paused) audio.pause();
    };

    window.addEventListener(PLAY_EVENT, handler as EventListener);
    return () =>
      window.removeEventListener(PLAY_EVENT, handler as EventListener);
  }, [groupId, playerId]);

  // Attach audio element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateDuration = () => {
      const d = audio.duration;
      if (Number.isFinite(d) && d > 0) setDurationSec(d);
    };

    const onLoadedMetadata = updateDuration;
    const onTimeUpdate = () => {
      // when scrubbing, we already set state explicitly; still ok to update
      setCurrentTimeSec(audio.currentTime || 0);
    };
    const onPlayEvent = () => setIsPlaying(true);
    const onPauseEvent = () => setIsPlaying(false);
    const onEndedEvent = () => {
      setIsPlaying(false);
      setCurrentTimeSec(0);
      onEnded?.();
    };

    const onErrorEvent = () => {
      console.error("[AudioPlayer] <audio> error", {
        srcProp: src,
        currentSrc: audio.currentSrc,
        networkState: audio.networkState,
        readyState: audio.readyState,
        mediaError: audio.error?.code,
      });
    };

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("durationchange", updateDuration);
    audio.addEventListener("loadeddata", updateDuration);
    audio.addEventListener("canplay", updateDuration);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("play", onPlayEvent);
    audio.addEventListener("pause", onPauseEvent);
    audio.addEventListener("ended", onEndedEvent);
    audio.addEventListener("error", onErrorEvent);
    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("durationchange", updateDuration);
      audio.removeEventListener("loadeddata", updateDuration);
      audio.removeEventListener("canplay", updateDuration);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("play", onPlayEvent);
      audio.removeEventListener("pause", onPauseEvent);
      audio.removeEventListener("ended", onEndedEvent);
      audio.removeEventListener("error", onErrorEvent);
    };
  }, [onEnded, src]);

  useEffect(() => {
    if (!onTimeChange) return;

    const dur = Number.isFinite(durationSec) ? durationSec : 0;
    const cur = Number.isFinite(currentTimeSec) ? currentTimeSec : 0;

    onTimeChange({
      currentTimeSec: cur,
      durationSec: dur,
      remainingSec: Math.max(0, dur - cur),
      progress01: dur ? clamp01(cur / dur) : 0,
      isPlaying,
    });
  }, [onTimeChange, currentTimeSec, durationSec, isPlaying]);

  useLayoutEffect(() => {
    const hasProvidedBars = Boolean(bars && bars.length > 0);
    if (loading || !src || hasProvidedBars) return;

    // show overlay immediately on first paint with new src
    setIsLoadingWaveform(true);
  }, [src, bars, loading]);

  // Waveform generation: use bars prop if present, else fetch+decode
  useEffect(() => {
    let cancelled = false;
    let retryCount = 0;
    const MAX_RETRIES = 5;

    const hasProvidedBars = Boolean(bars && bars.length > 0);
    setComputedBars(null);

    if (hasProvidedBars) {
      cleanupObjectUrl();
      setIsLoadingWaveform(false);
      return;
    }

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    if (loading || !src) {
      setIsLoadingWaveform(false);
      setComputedBars(null); // keep placeholder bars
      return;
    }
    async function run() {
      setIsLoadingWaveform(true);

      while (retryCount <= MAX_RETRIES && !cancelled) {
        try {
          const resp = await fetch(src);

          // Retry on transient errors
          if (!resp.ok) {
            const isRetriable =
              resp.status === 403 || resp.status === 429 || resp.status >= 500;

            if (isRetriable && retryCount < MAX_RETRIES) {
              retryCount++;
              const backoff = Math.min(
                1000 * Math.pow(2, retryCount - 1),
                8000
              );
              await sleep(backoff);
              continue; // retry
            }

            throw new Error(`HTTP ${resp.status}`);
          }

          const buf = await resp.arrayBuffer();
          if (cancelled) return;

          // OfflineAudioContext never touches the hardware audio session,
          // so it cannot interfere with <audio> playback on iOS.
          const OfflineCtx =
            (window as any).OfflineAudioContext ||
            (window as any).webkitOfflineAudioContext;
          const ctx = new OfflineCtx(1, 1, 44100);

          const audioBuffer: AudioBuffer = await new Promise(
            (resolve, reject) => {
              const p = ctx.decodeAudioData(buf.slice(0));
              if (p && typeof (p as any).then === "function") {
                (p as Promise<AudioBuffer>).then(resolve).catch(reject);
              } else {
                ctx.decodeAudioData(
                  buf.slice(0),
                  (ab: AudioBuffer) => resolve(ab),
                  (err: any) => reject(err)
                );
              }
            }
          );

          if (cancelled) return;

          setComputedBars(barsFromAudioBuffer(audioBuffer, effectiveBarCount));
          setDurationSec(audioBuffer.duration || 0);
          setIsLoadingWaveform(false);

          // audio.src is already set by the src-change effect — don't touch it here

          break; // success, exit retry loop
        } catch (error) {
          if (cancelled) return;

          if (retryCount < MAX_RETRIES) {
            retryCount++;
            const backoff = Math.min(1000 * Math.pow(2, retryCount - 1), 8000);
            await sleep(backoff);
            continue;
          }

          // All retries exhausted: fallback to placeholder bars, allow playback
          cleanupObjectUrl();
          setIsLoadingWaveform(false);
          break;
        }
      }
    }

    void run();

    return () => {
      cancelled = true;
      cleanupObjectUrl();
      setIsLoadingWaveform(false);
    };
  }, [src, bars, loading, effectiveBarCount, cleanupObjectUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !src) return;

    // Reset playback state only (don't touch computedBars; waveform effect handles it)
    setIsPlaying(false);
    setCurrentTimeSec(0);
    setDurationSec(0);
    setHasInteracted(false);

    // Set src directly — single source of truth, no <source> child conflict
    audio.pause();
    audio.src = src;
    audio.load();
  }, [src]);

  const showLoadingOverlay = loading || isLoadingWaveform;
  return (
    <FlexRow
      opacity={disabled ? 0.6 : 1}
      data-cy={dataCy}
      backgroundColor={colorPalette.popWhite[500]}
      outline={`1px solid ${colorPalette.popWhite[200]}`}
      borderRadius={8}
      px={8}
      py={4}
      gap={8}
      width="100%"
      position="relative"
    >
      {showLoadingOverlay && (
        <FlexRow
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          backgroundColor={colorPalette.popWhite[500]}
          outline={`1px solid ${colorPalette.popWhite[200]}`}
          borderRadius={8}
          px={8}
          py={4}
          gap={8}
          zIndex={2}
          width="100%"
          height="100%"
        >
          <AudioUploadRow>
            <AudioUploadBar />
          </AudioUploadRow>
        </FlexRow>
      )}
      <audio ref={audioRef} preload="auto" playsInline />

      <WaveformWrap
        ref={waveformRef}
        onPointerDown={onPointerDownWaveform}
        onPointerMove={onPointerMoveWaveform}
        onPointerUp={onPointerUpWaveform}
        role="slider"
        aria-label="audio-seek"
        aria-valuemin={0}
        aria-valuemax={Math.floor(durationSec || 0)}
        aria-valuenow={Math.floor(currentTimeSec || 0)}
      >
        <FlexRow gap={2} width="100%" height="100%">
          {effectiveBars.map((b, idx) => (
            <FlexColumn
              key={idx}
              flex="0 0 2px"
              width="2px"
              minWidth="2px"
              height={`${20 + b * 80}%`}
              backgroundColor={
                idx <= activeBarIndex
                  ? "background.neutral.default"
                  : "background.neutral.secondary-hover"
              }
              borderRadius={3}
            />
          ))}
        </FlexRow>
        {showDot && (
          <ProgressDot
            position="absolute"
            top="50%"
            left={`${Math.max(0, Math.min(100, progress01 * 100))}%`}
            width={8}
            height={8}
            borderRadius={999}
            backgroundColor="background.wishlink.default"
            cursor="pointer"
          />
        )}
      </WaveformWrap>
      <FlexRow
        flexShrink={0}
        justifyContent="flex-end"
        alignItems="center"
        gap={8}
        width="unset"
      >
        <Icon
          name={
            isPlaying ? "fa-solid fa-circle-pause" : "fa-solid fa-circle-play"
          }
          size="20"
          height="20"
          width="20"
          color="inherit"
          onClick={disabled ? undefined : togglePlayPause}
          aria-label={isPlaying ? "Pause audio" : "Play audio"}
          data-cy={`${dataCy}-play-pause`}
          cursor="pointer"
        />
        {showTime && timeMode !== "none" && (
          <FlexRow width="unset" gap={4}>
            <Box
              height={8}
              width={8}
              borderRadius={999}
              backgroundColor="background.wishlink.default"
            />
            <Body5RegularTypography
              color={colorPalette.popBlack[500]}
              width={26}
            >
              {displayTime}
            </Body5RegularTypography>
          </FlexRow>
        )}
      </FlexRow>
    </FlexRow>
  );
};
